import { ICardElementWithChildren } from "backend/model/model";
import { t } from "i18next";
import { IChartSettings, TChartSeries } from "qpr-base/configuration/types";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import React, { useRef } from "react";
import HighChart from "./Highchart";
import Legend from "./Legend";

const usePerformanceTrendStyles = makeThemedStyles(
  "PerformanceTrend",
  (theme) => ({
    root: {
      margin: "30px",
      padding: "20px",
      width: "220px",
      borderRadius: "4px",
      background:
        "linear-gradient(0deg, #E1F5FF 0%, rgba(225, 245, 255, 0) 100%)",
      color: "rgba(16, 41, 118, 1)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    },
    actualTargetContainer: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "nowrap",
      gap: "20px",
      marginBottom: "20px",
    },
    actualTarget: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "nowrap",
      gap: "5px",
    },
    title: {
      color: "rgba(16, 41, 118, 1)",
      fontWeight: "bold",
    },
    titleTypeName: {
      color: "rgba(16, 41, 118, 1)",
    },
    chartTitleStyle: {
      fontFamily: "Roboto, Helvetica",
      fontSize: "12px",
    },
    tooltip: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "70px",
      minWidth: "150px",
    },
    tooltipContent: {
      display: "flex",
      flexDirection: "column",
      padding: "20px 20px",
    },
    seriesLabel: {
      display: "flex",
      justifyContent: "start",
      gap: "5px",
      alignItems: "center",
      width: "100%",
    },
    seriesMarker: {
      width: "7px",
      height: "7px",
      borderRadius: "50%",
    },
    valuePoint: {
      color: "#000",
      fontWeight: 600,
    },
  })
);

export const PerformanceTrendTitle = (props: React.PropsWithChildren<{}>) => {
  const styles = usePerformanceTrendStyles();
  return <div className={styles.title}>{props.children}</div>;
};

export const PerformanceTrendTypeName = (
  props: React.PropsWithChildren<{}>
) => {
  const styles = usePerformanceTrendStyles();
  return <div className={styles.titleTypeName}>{props.children}</div>;
};

const filterSeries = (
  series: TChartSeries[],
  values: ICardElementWithChildren[]
): TChartSeries[] => {
  const filteredSeries: TChartSeries[] = [];
  for (let i = 0; i < series.length; i += 1) {
    if (
      values.find((v) => v[series[i].symbol as keyof ICardElementWithChildren])
    )
      filteredSeries.push(series[i]);
  }
  return filteredSeries;
};

export const PerformanceTrendChart = React.memo(
  (
    props: React.PropsWithChildren<{
      element: ICardElementWithChildren;
      values: ICardElementWithChildren[];
      settings: IChartSettings;
      period: string;
      width?: string;
      onSelect?: () => void;
      disableAnimation?: boolean;
      showLegend?: boolean;
    }>
  ) => {
    const styles = usePerformanceTrendStyles();
    const { values, settings } = props;
    const series = filterSeries(settings.series || [], values);
    const chartRef = useRef<Highcharts.Chart | null | undefined>(null);

    const isRangeLimit = (seriesSymbol: string): boolean => {
      return seriesSymbol === "TAR" || seriesSymbol === "ALA";
    };

    const getSeriesName = (name: string | undefined, symbol: string) => {
      if (name) return name;
      return t(`configtypes.${symbol}`);
    };

    const isValueMissing = (
      values: ICardElementWithChildren[],
      series: string,
      index: number
    ): boolean => {
      if (index < 0 || index >= values.length) return true;
      return (
        typeof values[index][series as keyof ICardElementWithChildren] ===
        "undefined"
      );
    };

    const hcOptions = React.useMemo(() => {
      return {
        credits: {
          enabled: false,
        },
        chart: {
          renderTo: "container",
          type: "areaspline",
          backgroundColor: "none",
          plotBackgroundColor: values.length
            ? "none"
            : {
                linearGradient: [0, 0, 0, 300],
                stops: [
                  [0, "rgba(240,240,240,1)"],
                  [1, "rgba(255,255,255, 0.1)"],
                ],
              },
          marginRight: 0,
          spacingRight: 0,
          spacingLeft: 0,
          height: "300px",
          animation: !props.disableAnimation,
          ignoreHiddenSeries: false,
          showAxes: true,
        },
        title: {
          text: "",
        },
        subtitle: {
          text: "",
        },
        // https://api.highcharts.com/highcharts/xAxis.crosshair
        xAxis: {
          tickInterval: 1,
          lineColor: values.length ? "transparent" : "rgba(242,242, 242,1)",
          showEmpty: true,
          labels: {
            formatter: function (this: any) {
              const v = values[this.value];

              return v?.period?.name || "";
            },
          },
        },
        yAxis: {
          offset: 30,
          gridLineWidth: 0,
          showEmpty: true,
          title: {
            text: ".",
          },
          min: 0,
          labels: {
            align: "left",
            style: {
              fontWeight: "bold",
              fontFamily: "Helvetica",
              fontSize: "14px",
              align: "left",
              textAlign: "left",
            },
          },
        },
        tooltip: {
          borderColor: "#ddd",
          borderRadius: 10,
          backgroundColor: "white",
          shape: "rect",
          padding: 0,
          shadow: {
            width: 10,
            color: "rgba(0,0,0,0.05)",
          },
          useHTML: true,
          formatter: function (this: any) {
            // HighCharts does not accept colors with spaces in between
            const colorWithoutSpaces = this.color.replace(/ /g, "");
            const value = values[this.x];
            const periodName = value?.period!.name || "";
            const valueWithoutSymbol = (value as any)[
              `${this.series.userOptions.seriesKey}_valuewithoutsymbol`
            ];
            return `<div class=${styles.tooltip} >
            <div class=${styles.tooltipContent}>
                  <div>${periodName}</div>
                  <div class=${styles.seriesLabel}>
                    <div class=${
                      styles.seriesMarker
                    } style=background-color:${colorWithoutSpaces}></div>
                  <div>${getSeriesName(
                    this.series.name,
                    this.series.symbol
                  )}:</div>
                    <div class=${styles.valuePoint} >${valueWithoutSymbol}</div>
                  </div>
                  </div>
              </div>`;
          } as any,
        },
        legend: {
          enabled: props.showLegend,
        },
        plotOptions: {
          series: {
            animation: !props.disableAnimation,
            connectNulls: false,
            label: {
              enabled: false,
            },
          },
          spline: {
            marker: {
              enabled: false,
              // see: https://api.highcharts.com/highcharts/plotOptions.series.marker
              // url(graphic.png)
              symbol: "circle",
              fillColor: "#FFFFFF",
              lineColor: "rgb(93,98,104)",
              lineWidth: 3,
              states: {
                hover: {
                  enabled: true,
                  radius: 10,
                },
              },
            },
          },
          areaspline: {
            lineWidth: 5,
            marker: {
              enabled: false,
              fillColor: "#FFFFFF",
              lineColor: "rgba(38, 166, 242, 0.8)",
              lineWidth: 3,
              states: {
                hover: {
                  enabled: true,
                  radius: 10,
                },
              },
            },
            shadow: false,
            states: {
              hover: {
                lineWidth: 5,
              },
            },
          },
        },
        series: series?.map((ser) => ({
          seriesKey: ser.symbol,
          connectNulls: false,
          label: {
            enabled: false,
          },
          name: t(`configtypes.${ser.symbol}`),
          zIndex: 1,
          type: "areaspline",
          visible: values.length > 0,
          lineWidth: isRangeLimit(ser.symbol) ? 4 : 5,
          color: ser.color,
          fillColor: isRangeLimit(ser.symbol)
            ? "none"
            : {
                linearGradient: [0, 0, 0, 300],
                stops: [
                  [0, ser.color],
                  [1, "rgba(255,255,255, 0.1)"],
                ],
              },
          data: values.length
            ? values
                .map((v) => {
                  return [
                    new Date(v.period!.startdate),
                    typeof v[ser.symbol as keyof ICardElementWithChildren] !==
                    "undefined"
                      ? Number(v[ser.symbol as keyof ICardElementWithChildren])
                      : null,
                  ];
                })
                .map((v, index) => {
                  const valuePoint: any = {
                    name: v[0],
                    y: v[1],
                  };
                  const prevMissing = isValueMissing(
                    values,
                    ser.symbol,
                    index - 1
                  );
                  const nextMissing = isValueMissing(
                    values,
                    ser.symbol,
                    index + 1
                  );
                  // add marker if "detached" valuepoint
                  if (v[1] !== null && prevMissing && nextMissing) {
                    valuePoint["marker"] = {
                      enabled: true,
                      lineWidth: 2,
                    };
                  }
                  return valuePoint;
                })
            : [0, 20, 40, 60, 80],
        })),
      };
    }, [values]);

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
        <div style={{ width: "100%", display: "flex", justifyContent: "end" }}>
          <Legend
            onMouseEnter={(i) => {
              chartRef.current?.series.forEach((ser, idx) => {
                if (idx !== i) {
                  chartRef?.current?.series[idx].setState("inactive");
                }
              });
            }}
            onMouseLeave={(i) => {
              chartRef.current?.series.forEach((ser, idx) => {
                chartRef?.current?.series[idx].setState("");
              });
            }}
            chartSeries={hcOptions?.series}
          />
        </div>
        <HighChart
          onInit={(ref) => {
            chartRef.current = ref.current;
          }}
          chartOptions={hcOptions}
        />
      </div>
    );
  }
);

export default PerformanceTrendChart;
